import { useEffect, useMemo } from 'react';
import { useNavigation, useSearchParams } from 'react-router';
import type { ShopperProducts } from '@/scapi';
import { useSelectedVariations } from '@/hooks/product/use-selected-variations';
import { isVariantProduct } from '@/lib/product-utils';
import { shouldOmitVariantPid } from '@/lib/wine-vintage';

type VariantWithInventory = ShopperProducts.schemas['Variant'] & {
    inventory?: ShopperProducts.schemas['Inventory'];
};

interface UseCurrentVariantProps {
    product: ShopperProducts.schemas['Product'];
    isChildProduct?: boolean;
    /**
     * Optional override forwarded to {@link useSelectedVariations}. Modal contexts pass their
     * local selection state here to avoid reading from / writing to the page URL.
     */
    selectionsOverride?: Record<string, string>;
}

/**
 * When the PDP loader already fetched a variant SKU (`type.variant`), prefer that full product
 * payload over the thin matching entry in `product.variants[]` (which usually has no inventory).
 * Only do this while the loaded SKU still matches the current attribute selection — otherwise keep
 * the thin match so swatch changes stay optimistic until the loader returns the new SKU.
 */
function toLoadedVariantProduct(
    product: ShopperProducts.schemas['Product'],
    matched: ShopperProducts.schemas['Variant']
): VariantWithInventory | undefined {
    if (!isVariantProduct(product) || !product.id || product.id !== matched.productId) {
        return undefined;
    }

    return {
        ...matched,
        productId: product.id,
        price: product.price ?? matched.price,
        orderable: product.inventory?.orderable ?? matched.orderable,
        variationValues: product.variationValues ?? matched.variationValues,
        inventory: product.inventory,
    };
}

/**
 * Determines the current product variant based on selected variation attributes and URL parameters.
 * Automatically syncs the variant selection with URL query parameters for shareable product links.
 *
 * @example Basic usage in ProductView
 * ```tsx
 * function ProductView({ product }) {
 *   const currentVariant = useCurrentVariant({ product });
 *
 *   return (
 *     <div>
 *       {currentVariant && (
 *         <p>Selected: {currentVariant.productId}</p>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 *
 * @param props - Configuration object
 * @param props.product - Product with variants array
 * @param props.isChildProduct - If true, prevents URL updates (useful for product sets/bundles)
 * @returns The matching variant object or undefined if no exact match is found
 */
export function useCurrentVariant({ product, isChildProduct = false, selectionsOverride }: UseCurrentVariantProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigation = useNavigation();

    // Use useSelectedVariations to get URL-aware selected attributes (or override-driven in modal usage)
    const selectedAttributes = useSelectedVariations({ product, isChildProduct, selectionsOverride });
    const variants = useMemo(() => product?.variants ?? [], [product]);

    // Find current variant based on selected attributes or URL param
    const currentVariant = useMemo(() => {
        const potentialVariants = variants.filter(
            ({ variationValues }) =>
                variationValues &&
                Object.keys(selectedAttributes).every((key) => {
                    return variationValues[key] === selectedAttributes[key];
                })
        );
        // If there's only 1 match, then we have narrowed down the true variant (meaning all of the variation attributes have been selected)
        const matched = potentialVariants.length === 1 ? potentialVariants[0] : undefined;
        if (!matched) {
            return undefined;
        }

        return toLoadedVariantProduct(product, matched) ?? matched;
    }, [selectedAttributes, variants, product]);

    // Update URL when variant changes. Skip when an override is supplied (modal usage) so swatch
    // changes there don't pollute the page URL or trigger route revalidation.
    //
    // Gift cards: selection is driven by `giftCardSize` only. Writing `pid` would revalidate the
    // PDP loader (including Page Designer aspect pages) and swap the master for a thinner variant
    // payload — which breaks cut switching when a PD page is published.
    //
    // The navigation.state === 'idle' guard avoids a race during the swatch click: while the
    // NavLink's nav to ?color=<new> is in flight, useSelectedVariations has already flipped
    // optimistically, so currentVariant points at the new variant — but searchParams is still
    // the canonical (old) URL. Building a new URL from that snapshot and calling setSearchParams
    // would supersede the in-flight nav with one that strips the user's color choice. Defer the
    // pid sync until the navigation has settled and searchParams reflects the new URL.
    useEffect(() => {
        if (isChildProduct || selectionsOverride || navigation.state !== 'idle' || !product?.id) {
            return;
        }

        if (shouldOmitVariantPid(product)) {
            // Gift cards / wine: selection is attribute-query only. Do not write pid —
            // pid sync revalidates the PDP loader and swaps the master for a thinner variant payload.
            return;
        }

        if (currentVariant && currentVariant.productId !== product.id) {
            const currentPid = searchParams.get('pid');
            // Only set pid if it's not already set to the correct value
            if (currentPid !== currentVariant.productId) {
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.set('pid', currentVariant.productId);
                setSearchParams(newSearchParams, { replace: true, preventScrollReset: true });
            }
        }
    }, [currentVariant, product, searchParams, setSearchParams, isChildProduct, selectionsOverride, navigation.state]);

    return currentVariant;
}
